import React, { useEffect } from "react";
import { useFrequentlyOrderSearchCriteriaStore } from "@/scenes/OrderManagement/store/FrequentlyOrderSearchCriteriaStore";
import TableSearchState from "@/scenes/OrderManagement/redux/states/TableSearchState";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import type OrderManagementForAllShopStore from "@/scenes/OrderManagementForAllShop/stores/OrderManagementForAllShopStore";
import PremiumValidator, { PremiumFunctionType } from "@/services/PremiumValidator";
import { useGlobalStore, useOrderMarkStore, useShippingAreaStore } from "@/store/hooks";
import { showShopPremiumModal } from "@/services/http/utils";
import { observer } from "mobx-react-lite";
import { deprecatedQuicks } from "@/scenes/OrderManagement/enum/QuickEnum";
import { Button, message, Popover } from "antd";
import OrderTag from "@/enum/OrderTag";
import { remove } from "lodash";
import PrintStatus from "@/scenes/OrderManagement/enum/PrintStatus";
import { OrderTagSearchType } from "../OrderTagSelectField";
import { FrequentlyUsedOrderSearchEntity } from "../FrequentlyUsedOrderSearchCriteriaModal";
import moment from "moment";
import { isEmpty } from "@/utils";
import DraggableList from "@/components/DraggableList";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";

interface SortableButtonListProps {
    entityId: number;
    entityType: FrequentlyUsedOrderSearchEntity;
}

const SortableButtonList: React.FC<SortableButtonListProps> = function (props) {
    const { entityId, entityType } = props;

    const globalStore = useGlobalStore();
    const orderManagementStore = useOrderManagementStore();
    const { loadList: loadShippingAreaList } = useShippingAreaStore();
    const { loadList: loadOrderMarkList } = useOrderMarkStore();
    const {
        loadFrequentlyOrderSearchCriterion,
        frequentlyOrderSearchCriterions: frequentlyOrderSearchCriterion,
        clearFrequentlyOrderSearchCriterions: clearFrequentlyOrderSearchCriterion,
        handleSort,
    } = useFrequentlyOrderSearchCriteriaStore();

    useEffect(() => {
        loadFrequentlyOrderSearchCriterion({
            companyId: entityType === FrequentlyUsedOrderSearchEntity.COMPANY ? entityId : null,
            shopId: entityType === FrequentlyUsedOrderSearchEntity.SHOP ? entityId : null,
        });

        return () => {
            clearFrequentlyOrderSearchCriterion();
        };
    }, [entityId]);

    function transformTimeRange(values: TableSearchState) {
        if (!values?.timeType) return;

        const today = moment().startOf("day");

        const buildDateTime = (offset?: number, timeStr?: string) => {
            if (isEmpty(offset) || !timeStr) return undefined;
            const base = today.clone().add(offset, "day");
            const time = moment(timeStr, "HH:mm:ss");
            return base
                .set({
                    hour: time.hour(),
                    minute: time.minute(),
                    second: time.second(),
                })
                .format("YYYY-MM-DD HH:mm:ss");
        };

        values.timeFrom = buildDateTime(values.timeFromOffset, values.timeFrom);
        values.timeTo = buildDateTime(values.timeToOffset, values.timeTo);

        delete values.timeFromOffset;
        delete values.timeToOffset;
    }

    async function checkPremiumPermission(entityType, entityId, index) {
        if (entityType !== FrequentlyUsedOrderSearchEntity.SHOP) return true;

        const { isPremium } = await PremiumValidator.validateShopVersion(entityId, globalStore);
        if (!isPremium && index) {
            const shopName = globalStore.shopMapping.get(entityId)?.name;
            showShopPremiumModal(
                `【${shopName}】店铺是标准版本，最多只能添加和使用1个常用搜索条件，需要使用更多则欢迎【${shopName}】店铺补差价升级后使用`,
                entityId,
                PremiumFunctionType.FREQUENTLY_USED_ORDER_SEARCH_CRITERIA,
            );
            return false;
        }
        return true;
    }

    function parseSearchParams(data: string): TableSearchState {
        try {
            return JSON.parse(data);
        } catch (err) {
            console.error("解析搜索条件失败", err);
            return {};
        }
    }

    function transformDeprecatedFields(params: TableSearchState) {
        if (params.quick) {
            if (deprecatedQuicks.includes(params.quick)) {
                message.warning("该常用搜索包含过时的搜索项，请删除并重新创建一个新的常用搜索");
            }
            params.quicks = [params.quick];
            delete params.quick;
        }

        if ((params.printStatus as any) === "printWaybillCount") {
            message.warning("该常用搜索包含过时的搜索项【打印状态】，请删除并重新创建一个新的常用搜索");
            params.printStatus = PrintStatus.PRINT_WAYBILL_COUNT_GT_1;
        }
    }

    function transformSkuFields(params: TableSearchState) {
        if (params?.skuProperties) {
            params.skuPropertiesValue = { skuProperties: params.skuProperties };
        }
        if (params?.orderItemTitle) {
            params.orderItemTitleValue = { orderItemTitle: params.orderItemTitle };
        }
        if (params.orderItemExternalId) {
            params.orderItemExternalIdValue = { orderItemExternalId: params.orderItemExternalId };
        }
    }

    function transformTagFields(params: TableSearchState) {
        if (params.orderTags?.includes(OrderTag.DOUDIAN_DELIVERY_APPOINTMENT)) {
            remove(params.orderTags, (tag) => tag === OrderTag.DOUDIAN_DELIVERY_APPOINTMENT);
            params.orderTags.push(OrderTag.DOUDIAN_APPOINTMENT_DELIVERY);
        }
        if (params.orderTags?.includes(OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL)) {
            remove(params.orderTags, (tag) => tag === OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL);
            params.orderTags.push(OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL_V2);
        }
        if (params.orderTagSearchType === OrderTagSearchType.UnInclude) {
            params.excludedOrderTags = params.orderTags;
            params.orderTags = undefined;
        }
    }

    async function triggerSideEffects(params: TableSearchState) {
        if (params?.shippingAreaId) {
            await loadShippingAreaList();
        }
        if (params?.markIds) {
            await loadOrderMarkList();
        }
        if (params.provinceCode) {
            await globalStore.areaStore.ensureFhyArea();
        }
    }

    function applySearchParams(params: TableSearchState) {
        if (entityType === FrequentlyUsedOrderSearchEntity.SHOP) {
            orderManagementStore.table.resetFormValues();
        } else {
            (orderManagementStore as OrderManagementForAllShopStore).table.resetFormValues(["shopIds"]);
        }

        orderManagementStore.table.form.setFieldsValue(params);
        orderManagementStore.table.changePageNo(1);
    }

    function fetchOrders(entityType: FrequentlyUsedOrderSearchEntity, entityId: number) {
        if (entityType === FrequentlyUsedOrderSearchEntity.SHOP) {
            orderManagementStore.fetchOrders(entityId);
        } else {
            (orderManagementStore as OrderManagementForAllShopStore).fetchOrders();
        }
    }

    async function handleSearch(id: number, data: string, index: number) {
        // 校验权限
        if (!(await checkPremiumPermission(entityType, entityId, index))) return;

        // 解析 JSON
        const params = parseSearchParams(data);

        // 转换参数
        transformTimeRange(params);
        transformDeprecatedFields(params);
        transformSkuFields(params);
        transformTagFields(params);

        // 加载懒加载的数据
        await triggerSideEffects(params);

        // 搜索
        applySearchParams(params);
        fetchOrders(entityType, entityId);
        orderManagementStore.table.setActiveCriteriaId(id);

        try {
            const shop = globalStore.shopMapping.get(entityId);
            collectEvent(DataTrackEvent.CLICK_FREQUENT_SEARCH, {
                shop_id: entityType === FrequentlyUsedOrderSearchEntity.COMPANY ? undefined : entityId,
                shop_name: entityType === FrequentlyUsedOrderSearchEntity.COMPANY ? undefined : shop?.name,
                shop_type:
                    entityType === FrequentlyUsedOrderSearchEntity.COMPANY ? undefined : ShopTypeNameMap[shop?.type],
                company_id: entityType === FrequentlyUsedOrderSearchEntity.COMPANY ? entityId : undefined,
                params: data,
            });
        } catch {}
    }

    const activeCriteriaId = orderManagementStore.table.activeCriteriaId;

    useEffect(() => {
        orderManagementStore.table.setActiveCriteriaId(null);
    }, [orderManagementStore.tab, orderManagementStore.shopId]);

    return (
        <DraggableList
            itemKey="id"
            data={frequentlyOrderSearchCriterion}
            onDragEnd={handleSort}
            renderItem={({ id, name, data }, { ref, opacity }, index) => {
                const active = activeCriteriaId === id;

                let showAnyMatchTip = false;

                if (active) {
                    const params = parseSearchParams(data);

                    if (params.anyMatch) {
                        showAnyMatchTip = true;
                    }
                }

                const content = (
                    <span
                        key={id}
                        style={{
                            opacity,
                            padding: "3px",
                            borderRadius: "2px",
                            color: "#337ab7",
                            transition: "all 0.1s linear",
                            backgroundColor: active ? "rgb(226,246,255)" : null,
                        }}
                        ref={ref}
                        className="fs12 ml5 cursor-pointer"
                        onClick={() => {
                            handleSearch(id, data, index);
                        }}
                    >
                        {name}
                    </span>
                );

                if (showAnyMatchTip) {
                    return (
                        <Popover content="任一条件匹配" key={id}>
                            {content}
                        </Popover>
                    );
                }

                return content;
            }}
        />
    );
};

export default observer(SortableButtonList);
